import type { DraftDto, DraftKind, MailIdentity, Message } from "@/api/mailApi";

// Shape of state the ComposePane is opened with. Reply / forward all use the
// same machinery — only the initial values differ.
export interface ComposeSeed {
    kind: DraftKind;
    identityId: string | null;
    to: string;
    cc: string;
    bcc: string;
    subject: string;
    bodyHtml: string;
    inReplyToMessageId: string | null;
    attachmentIds: string[];
}

export function seedForNew(identities: MailIdentity[]): ComposeSeed {
    return {
        kind: "New",
        identityId: defaultIdentityId(identities),
        to: "",
        cc: "",
        bcc: "",
        subject: "",
        bodyHtml: signatureBlock(defaultIdentity(identities)),
        inReplyToMessageId: null,
        attachmentIds: []
    };
}

export function seedForReply(
    message: Message,
    identities: MailIdentity[],
    replyAll: boolean
): ComposeSeed {
    const me = defaultIdentity(identities);
    const replyTo = message.from.email;

    let cc = "";
    if (replyAll) {
        const extras = message.to
            .map((r) => r.email)
            .filter((email) => email && email.toLowerCase() !== (me?.emailAddress ?? "").toLowerCase())
            .filter((email) => email.toLowerCase() !== replyTo.toLowerCase());
        cc = extras.join(", ");
    }

    return {
        kind: replyAll ? "ReplyAll" : "Reply",
        identityId: me?.id ?? null,
        to: replyTo,
        cc,
        bcc: "",
        subject: ensurePrefix("Re:", message.subject),
        bodyHtml: signatureBlock(me) + buildQuote(message),
        inReplyToMessageId: message.id,
        attachmentIds: []
    };
}

// Hydrate compose from a saved draft so clicking a Drafts-folder row reopens
// exactly what was last stored, including any unsent quoted body.
export function seedForDraft(draft: DraftDto): ComposeSeed {
    return {
        kind: draft.kind,
        identityId: draft.identityId,
        to: draft.to,
        cc: draft.cc,
        bcc: draft.bcc,
        subject: draft.subject,
        bodyHtml: draft.bodyHtml,
        inReplyToMessageId: draft.inReplyToMessageId,
        attachmentIds: draft.attachmentIds
    };
}

export function seedForForward(message: Message, identities: MailIdentity[]): ComposeSeed {
    const me = defaultIdentity(identities);
    return {
        kind: "Forward",
        identityId: me?.id ?? null,
        to: "",
        cc: "",
        bcc: "",
        subject: ensurePrefix("Fwd:", message.subject),
        bodyHtml: signatureBlock(me) + buildQuote(message),
        inReplyToMessageId: message.id,
        attachmentIds: []
    };
}

function defaultIdentity(identities: MailIdentity[]): MailIdentity | undefined {
    return identities.find((i) => i.isDefault) ?? identities[0];
}

function defaultIdentityId(identities: MailIdentity[]): string | null {
    return defaultIdentity(identities)?.id ?? null;
}

export function signatureBlock(identity: MailIdentity | undefined): string {
    if (!identity?.signatureHtml) return "<p><br /></p>";
    return `<p><br /></p><div data-athavita-signature="true">${identity.signatureHtml}</div>`;
}

function ensurePrefix(prefix: string, subject: string): string {
    const trimmed = subject.trim();
    if (trimmed.toLowerCase().startsWith(prefix.toLowerCase())) return trimmed;
    return `${prefix} ${trimmed}`;
}

function buildQuote(message: Message): string {
    const sent = new Date(message.receivedAt).toLocaleString();
    const fromLine = message.from.name
        ? `${escapeHtml(message.from.name)} &lt;${escapeHtml(message.from.email)}&gt;`
        : escapeHtml(message.from.email);
    return [
        `<hr />`,
        `<p><strong>From:</strong> ${fromLine}<br />`,
        `<strong>Sent:</strong> ${escapeHtml(sent)}<br />`,
        `<strong>Subject:</strong> ${escapeHtml(message.subject)}</p>`,
        `<blockquote style="border-left: 2px solid #ccc; padding-left: 0.75rem; color: #555;">`,
        message.bodyHtml,
        `</blockquote>`
    ].join("\n");
}

function escapeHtml(value: string): string {
    return value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}
